import { useEffect, useState, type ReactNode } from "react";
import { Button, Icon } from "@qota/ui";
import { platform } from "../lib/installed";
import { INSET_TOP, CONTENT_BOTTOM } from "../lib/insets";

/**
 * La schermata che chiede di installare Qota prima di usarla.
 *
 * ── Perché esiste ─────────────────────────────────────────────────────────
 *
 * Perché in una scheda di Safari su iPhone **le notifiche push non esistono**:
 * non è una limitazione dell'app, è che l'API non c'è proprio. Metà di quello
 * che Qota promette — «ti avviso quando Gioia aggiunge una spesa» — non può
 * funzionare, e l'unico modo onesto di gestirlo è chiederlo prima, non
 * scusarsene dopo.
 *
 * ── Chi non passa di qui ──────────────────────────────────────────────────
 *
 * Chi apre un **link di invito**: è l'unico ingresso di chi l'app non ce l'ha,
 * e sbarrarlo significherebbe che in un gruppo non entra più nessuno. Chi è su
 * un computer, dove le PWA si installano male e le notifiche non le aspetta
 * nessuno. E la diagnostica, che serve proprio quando qualcosa non parte.
 */

/** Su Android il browser sa installare da sé: l'evento arriva prima del bisogno. */
interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
}

export function InstallWall() {
  const os = platform();
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);

  useEffect(() => {
    // `beforeinstallprompt` esiste solo su Chrome e derivati: dove c'è, il
    // pulsante installa davvero invece di spiegare a parole.
    const capture = (event: Event) => {
      event.preventDefault();
      setPrompt(event as InstallPrompt);
    };

    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "var(--surface-canvas)",
        padding: `calc(${INSET_TOP} + 24px) var(--space-5) ${CONTENT_BOTTOM}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <img src="/icons/icon-192.png" alt="" width={64} height={64} style={{ borderRadius: 18 }} />

      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 27,
          fontWeight: 600,
          letterSpacing: "-0.03em",
          lineHeight: "32px",
          margin: "20px 0 0",
          textWrap: "balance",
        }}
      >
        Aggiungi Qota alla schermata Home
      </h1>

      <p
        style={{
          fontSize: 13.5,
          lineHeight: "20px",
          color: "var(--text-secondary)",
          margin: "10px 0 0",
          textWrap: "pretty",
        }}
      >
        {os === "ios"
          ? "Su iPhone le notifiche funzionano solo dall'app installata: in una scheda del browser non arrivano, e non è una scelta nostra. Ci vogliono dieci secondi."
          : "Installata parte più in fretta, funziona senza rete e può avvisarti quando qualcuno aggiunge una spesa."}
      </p>

      {prompt ? (
        <div style={{ marginTop: 26 }}>
          <Button variant="accent" size="lg" full onClick={() => void prompt.prompt()}>
            Installa Qota
          </Button>
        </div>
      ) : (
        <ol
          style={{
            margin: "26px 0 0",
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {(os === "ios" ? IOS_STEPS : ANDROID_STEPS).map((step, index) => (
            <li key={index} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  background: "var(--surface-accent)",
                  display: "grid",
                  placeItems: "center",
                  fontFamily: "var(--font-numeric)",
                  fontSize: 11,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </span>
              <span style={{ fontSize: 13.5, lineHeight: "19px" }}>{step}</span>
            </li>
          ))}
        </ol>
      )}

      <p
        style={{
          fontSize: 11.5,
          lineHeight: "17px",
          color: "var(--text-tertiary)",
          margin: "26px 0 0",
          textWrap: "pretty",
        }}
      >
        Hai un invito da aprire? Il link funziona anche da qui: aprilo, e potrai installare l'app
        dopo essere entrato nel gruppo.
      </p>

      <div style={{ marginTop: 14 }}>
        <Button
          variant="ghost"
          size="md"
          iconLeft={<Icon name="refresh-cw" size={16} />}
          onClick={() => window.location.reload()}
        >
          L'ho installata
        </Button>
      </div>
    </div>
  );
}

const IOS_STEPS: ReactNode[] = [
  <>
    Tocca <strong>Condividi</strong> nella barra in basso di Safari
  </>,
  <>
    Scorri e scegli <strong>Aggiungi a Home</strong>
  </>,
  <>
    Conferma con <strong>Aggiungi</strong>, poi apri Qota dall'icona
  </>,
];

const ANDROID_STEPS: ReactNode[] = [
  <>
    Apri il menu <strong>⋮</strong> del browser
  </>,
  <>
    Scegli <strong>Installa app</strong> o <strong>Aggiungi a schermata Home</strong>
  </>,
  <>Apri Qota dall'icona appena creata</>,
];
